/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { UapiMcpCore } from "../core.js";
import { appendForm, encodeFormQuery } from "../lib/encodings.js";
import {
  getContentTypeFromFileName,
  readableStreamToArrayBuffer,
} from "../lib/files.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import { APIError } from "../models/errors/apierror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import {
  PostImageCompressRequest,
  PostImageCompressRequest$zodSchema,
} from "../models/postimagecompressop.js";
import { APICall, APIPromise } from "../types/async.js";
import { isBlobLike } from "../types/blobs.js";
import { Result } from "../types/fp.js";
import { isReadableStream } from "../types/streams.js";

export enum PostImageCompressAcceptEnum {
  applicationJsonAccept = "application/json",
  imageWildcardAccept = "image/*",
}

/**
 * 无损压缩图片
 *
 * @remarks
 * 还在为图片体积和加载速度发愁吗？体验一下我们强大的**无损压缩服务**，它能在几乎不牺牲任何肉眼可感知的画质的前提下，将图片体积压缩到极致。
 *
 * ## 功能概述
 * 你只需要上传一张常见的图片（如 PNG, JPG），选择一个压缩等级，就能获得一个体积小到惊人的压缩文件。这对于需要大量展示高清图片的网站、App 或小程序来说，是优化用户体验、节省带宽和存储成本的利器。
 *
 * ## 使用须知
 * > [!TIP]
 * > 为了给您最好的压缩效果，我们的算法需要进行复杂计算，处理时间可能会稍长一些，请耐心等待。
 *
 * > [!WARNING]
 * > **服务排队提醒**
 * > 这是一个计算密集型服务。在高并发时，您的请求可能会被排队等待处理。如果您需要将其集成到对延迟敏感的生产服务中，请注意这一点。
 *
 * ### 请求与响应格式
 * - 请求必须使用 `multipart/form-data` 格式上传文件。
 * - 成功响应将直接返回压缩后的文件二进制流 (`image/*`)，并附带 `Content-Disposition` 头，建议客户端根据此头信息保存文件。
 *
 * ## 参数详解
 * ### `level` (压缩等级)
 * 这是一个从 `1` 到 `5` 的整数，它决定了压缩的强度和策略，数字越小，压缩率越高。所有等级都经过精心调校，以在最大化压缩率的同时保证出色的视觉质量。
 * - `1`: **极限压缩** (推荐，体积最小，画质优异)
 * - `2`: **高效压缩**
 * - `3`: **智能均衡** (默认选项)
 * - `4`: **画质优先**
 * - `5`: **专业保真** (压缩率稍低，保留最多图像信息)
 *
 * ## 错误处理指南
 * - **400 Bad Request**: 通常因为没有上传文件，或者 `level` 参数不在 1-5 的范围内。
 * - **500 Internal Server Error**: 如果在压缩过程中服务器发生内部错误，会返回此状态码。
 */
export function imagePostImageCompress(
  client$: UapiMcpCore,
  request: PostImageCompressRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    Response,
    | APIError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
> {
  return new APIPromise($do(
    client$,
    request,
    options,
  ));
}

async function $do(
  client$: UapiMcpCore,
  request: PostImageCompressRequest,
  options?: RequestOptions & {
    acceptHeaderOverride?: PostImageCompressAcceptEnum;
  },
): Promise<
  [
    Result<
      Response,
      | APIError
      | SDKValidationError
      | UnexpectedClientError
      | InvalidRequestError
      | RequestAbortedError
      | RequestTimeoutError
      | ConnectionError
    >,
    APICall,
  ]
> {
  const parsed$ = safeParse(
    request,
    (value$) => PostImageCompressRequest$zodSchema.parse(value$),
    "Input validation failed",
  );
  if (!parsed$.ok) {
    return [parsed$, { status: "invalid" }];
  }
  const payload$ = parsed$.value;
  const body$ = new FormData();
  if (isBlobLike(payload$.body.file)) {
    appendForm(body$, "file", payload$.body.file);
  } else if (isReadableStream(payload$.body.file.content)) {
    const buffer = await readableStreamToArrayBuffer(
      payload$.body.file.content,
    );
    const contentType = getContentTypeFromFileName(payload$.body.file.fileName)
      || "application/octet-stream";
    const blob = new Blob([buffer], { type: contentType });
    appendForm(body$, "file", blob, payload$.body.file.fileName);
  } else {
    const contentType = getContentTypeFromFileName(payload$.body.file.fileName)
      || "application/octet-stream";
    appendForm(
      body$,
      "file",
      new Blob([payload$.body.file.content], { type: contentType }),
      payload$.body.file.fileName,
    );
  }
  const path$ = pathToFunc("/image/compress")();
  const query$ = encodeFormQuery({
    "format": payload$.format,
    "level": payload$.level,
  });

  const headers$ = new Headers(compactMap({
    Accept: options?.acceptHeaderOverride
      || "application/json;q=1, image/*;q=0",
  }));
  const securityInput = await extractSecurity(client$._options.security);
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client$._options,
    baseURL: options?.serverURL ?? client$._baseURL ?? "",
    operationID: "post-image-compress",
    oAuth2Scopes: null,
    resolvedSecurity: requestSecurity,
    securitySource: client$._options.security,
    retryConfig: options?.retries
      || client$._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || [
      "429",
      "500",
      "502",
      "503",
      "504",
    ],
  };

  const requestRes = client$._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path$,
    headers: headers$,
    query: query$,
    body: body$,
    userAgent: client$._options.userAgent,
    timeoutMs: options?.timeoutMs || client$._options.timeoutMs
      || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req$ = requestRes.value;

  const doResult = await client$._do(req$, {
    context,
    errorCodes: [],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req$ }];
  }
  return [doResult, {
    status: "complete",
    "request": req$,
    response: doResult.value,
  }];
}
